import { useState, useEffect, useContext } from 'react';
import {
    Shield,
    Users,
    UserPlus,
    Trash2,
    CheckCircle,
    XCircle,
    AlertTriangle,
    RefreshCw,
    X,
    Eye,
    EyeOff,
    Clock,
    FileText
} from 'lucide-react';
import { authApi, deleteRequestApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function AdminPortal() {
    const { user, isAuthenticated } = useAuth();
    const [activeTab, setActiveTab] = useState('users');
    const [users, setUsers] = useState([]);
    const [deleteRequests, setDeleteRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

    // Modal states
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [showEditUser, setShowEditUser] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showReviewRequest, setShowReviewRequest] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);

    // Form states
    const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'doctor' });
    const [editForm, setEditForm] = useState({ name: '', role: '', is_active: true });
    const [reviewNotes, setReviewNotes] = useState('');
    const [formLoading, setFormLoading] = useState(false);

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const showNotification = (message, type = 'success') => {
        setNotification({ show: true, message, type });
        setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'users') {
                const response = await authApi.getUsers();
                setUsers(response.data);
            } else if (activeTab === 'delete-requests') {
                const response = await deleteRequestApi.getAll();
                setDeleteRequests(response.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showNotification('Error fetching data', 'error');
        } finally {
            setLoading(false);
        }
    };

    // User Management Functions
    const handleCreateUser = async (e) => {
        e.preventDefault();
        setFormLoading(true);
        try {
            await authApi.createUser(createForm);
            showNotification('User created successfully!');
            setShowCreateUser(false);
            setCreateForm({ name: '', email: '', password: '', role: 'doctor' });
            fetchData();
        } catch (error) {
            showNotification(error.response?.data?.detail || 'Error creating user', 'error');
        } finally {
            setFormLoading(false);
        }
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        if (!selectedUser) return;
        setFormLoading(true);
        try {
            await authApi.updateUser(selectedUser.id, editForm);
            showNotification('User updated successfully!');
            setShowEditUser(false);
            setSelectedUser(null);
            fetchData();
        } catch (error) {
            showNotification(error.response?.data?.detail || 'Error updating user', 'error');
        } finally {
            setFormLoading(false);
        }
    };

    const handleToggleUserStatus = async (userId, currentStatus) => {
        try {
            if (currentStatus) {
                await authApi.deactivateUser(userId);
                showNotification('User deactivated');
            } else {
                await authApi.activateUser(userId);
                showNotification('User activated');
            }
            fetchData();
        } catch (error) {
            showNotification(error.response?.data?.detail || 'Error updating user status', 'error');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            await authApi.deleteUser(userId);
            showNotification('User deleted successfully');
            fetchData();
        } catch (error) {
            showNotification(error.response?.data?.detail || 'Error deleting user', 'error');
        }
    };

    const openEditUser = (userItem) => {
        setSelectedUser(userItem);
        setEditForm({ name: userItem.name, role: userItem.role, is_active: userItem.is_active });
        setShowEditUser(true);
    };

    // Delete Request Functions
    const openReviewRequest = (request) => {
        setSelectedRequest(request);
        setReviewNotes('');
        setShowReviewRequest(true);
    };

    const handleApproveRequest = async () => {
        if (!selectedRequest) return;
        setFormLoading(true);
        try {
            await deleteRequestApi.approve(selectedRequest.id, reviewNotes || null);
            showNotification('Request approved and patient deleted');
            setShowReviewRequest(false);
            setSelectedRequest(null);
            fetchData();
        } catch (error) {
            showNotification(error.response?.data?.detail || 'Error approving request', 'error');
        } finally {
            setFormLoading(false);
        }
    };

    const handleRejectRequest = async () => {
        if (!selectedRequest) return;
        if (!reviewNotes.trim()) {
            showNotification('Please provide a reason for rejection', 'error');
            return;
        }
        setFormLoading(true);
        try {
            await deleteRequestApi.reject(selectedRequest.id, reviewNotes);
            showNotification('Request rejected');
            setShowReviewRequest(false);
            setSelectedRequest(null);
            fetchData();
        } catch (error) {
            showNotification(error.response?.data?.detail || 'Error rejecting request', 'error');
        } finally {
            setFormLoading(false);
        }
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'admin': return 'bg-purple-100 text-purple-700';
            case 'doctor': return 'bg-blue-100 text-blue-700';
            case 'patient': return 'bg-green-100 text-green-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Pending</span>;
            case 'approved':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Approved</span>;
            case 'rejected':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Rejected</span>;
            default:
                return null;
        }
    };

    // Check if user is admin
    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
                    <div className="p-4 bg-red-100 rounded-full w-fit mx-auto mb-4">
                        <Shield className="w-12 h-12 text-red-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
                    <p className="text-gray-600">
                        You need administrator privileges to access this portal.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
            {/* Notification */}
            {notification.show && (
                <div className={`fixed top-4 right-4 z-[100] px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 animate-slide-in ${
                    notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                }`}>
                    {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                    <span className="font-medium">{notification.message}</span>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl shadow-lg">
                            <Shield className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Admin Portal</h1>
                            <p className="text-gray-600">Manage users, doctors, and review requests</p>
                        </div>
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                        <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6">
                    <button
                        onClick={() => setActiveTab('users')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                            activeTab === 'users'
                                ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <Users className="w-5 h-5" />
                        User Management
                    </button>
                    <button
                        onClick={() => setActiveTab('delete-requests')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                            activeTab === 'delete-requests'
                                ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <Trash2 className="w-5 h-5" />
                        Delete Requests
                        {deleteRequests.filter(r => r.status === 'pending').length > 0 && (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                                {deleteRequests.filter(r => r.status === 'pending').length}
                            </span>
                        )}
                    </button>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4" />
                            <p className="text-gray-600">Loading...</p>
                        </div>
                    </div>
                ) : activeTab === 'users' ? (
                    <div>
                        {/* Add User Button */}
                        <div className="mb-6">
                            <button
                                onClick={() => setShowCreateUser(true)}
                                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all"
                            >
                                <UserPlus className="w-5 h-5" />
                                Add User
                            </button>
                        </div>

                        {/* Users Table */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">User</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Role</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Created</th>
                                        <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {users.map((userItem) => (
                                        <tr key={userItem.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div>
                                                    <p className="font-semibold text-gray-900">{userItem.name}</p>
                                                    <p className="text-sm text-gray-500">{userItem.email}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(userItem.role)}`}>
                                                    {userItem.role?.charAt(0).toUpperCase() + userItem.role?.slice(1)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {userItem.is_active ? (
                                                    <span className="flex items-center gap-1 text-green-600">
                                                        <CheckCircle className="w-4 h-4" />
                                                        Active
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center gap-1 text-red-600">
                                                        <XCircle className="w-4 h-4" />
                                                        Inactive
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {new Date(userItem.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => openEditUser(userItem)}
                                                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                                        title="Edit"
                                                    >
                                                        <FileText className="w-4 h-4" />
                                                    </button>
                                                    {userItem.id !== user?.id && (
                                                        <>
                                                            <button
                                                                onClick={() => handleToggleUserStatus(userItem.id, userItem.is_active)}
                                                                className={`p-2 rounded-lg transition-colors ${
                                                                    userItem.is_active
                                                                        ? 'text-amber-600 hover:bg-amber-50'
                                                                        : 'text-green-600 hover:bg-green-50'
                                                                }`}
                                                                title={userItem.is_active ? 'Deactivate' : 'Activate'}
                                                            >
                                                                {userItem.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteUser(userItem.id)}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Delete"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {deleteRequests.length === 0 ? (
                            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                                <Trash2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-2">No Delete Requests</h3>
                                <p className="text-gray-500">There are no patient deletion requests at this time.</p>
                            </div>
                        ) : (
                            deleteRequests.map((request) => (
                                <div key={request.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="font-bold text-gray-900">{request.patient_name}</h3>
                                                {getStatusBadge(request.status)}
                                            </div>
                                            <p className="text-sm text-gray-600 mb-2">
                                                <strong>Reason:</strong> {request.reason}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                Requested by: {request.requested_by_name} on {new Date(request.created_at).toLocaleString()}
                                            </p>
                                            {request.reviewed_by_name && (
                                                <p className="text-sm text-gray-500 mt-1">
                                                    Reviewed by: {request.reviewed_by_name} on {new Date(request.reviewed_at).toLocaleString()}
                                                </p>
                                            )}
                                            {request.review_notes && (
                                                <p className="text-sm text-gray-600 mt-2">
                                                    <strong>Review Notes:</strong> {request.review_notes}
                                                </p>
                                            )}
                                        </div>
                                        {request.status === 'pending' && (
                                            <button
                                                onClick={() => openReviewRequest(request)}
                                                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                                            >
                                                Review
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Create User Modal */}
                {showCreateUser && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
                            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-900">Create New User</h2>
                                <button onClick={() => setShowCreateUser(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={createForm.name}
                                        onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        required
                                        value={createForm.email}
                                        onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                    <input
                                        type="password"
                                        required
                                        minLength={6}
                                        value={createForm.password}
                                        onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                    <select
                                        value={createForm.role}
                                        onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                                    >
                                        <option value="doctor">Doctor</option>
                                        <option value="patient">Patient</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateUser(false)}
                                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={formLoading}
                                        className="flex-1 px-4 py-2 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 disabled:opacity-50"
                                    >
                                        {formLoading ? 'Creating...' : 'Create User'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Edit User Modal */}
                {showEditUser && selectedUser && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
                            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-gray-900">Edit User</h2>
                                <button onClick={() => setShowEditUser(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                            <form onSubmit={handleEditUser} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={editForm.name}
                                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                    <select
                                        value={editForm.role}
                                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                                    >
                                        <option value="doctor">Doctor</option>
                                        <option value="patient">Patient</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowEditUser(false)}
                                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={formLoading}
                                        className="flex-1 px-4 py-2 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 disabled:opacity-50"
                                    >
                                        {formLoading ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Review Delete Request Modal */}
                {showReviewRequest && selectedRequest && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
                            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-amber-50 rounded-t-2xl">
                                <div className="flex items-center gap-3">
                                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                                    <h2 className="text-xl font-bold text-gray-900">Review Delete Request</h2>
                                </div>
                                <button onClick={() => setShowReviewRequest(false)} className="p-2 hover:bg-amber-100 rounded-lg">
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                            <div className="p-6">
                                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                                    <p className="font-semibold text-gray-900">{selectedRequest.patient_name}</p>
                                    <p className="text-sm text-gray-600 mt-1">
                                        <strong>Reason:</strong> {selectedRequest.reason}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Requested by: {selectedRequest.requested_by_name}
                                    </p>
                                </div>

                                <div className="mb-4 p-4 bg-red-50 border border-red-100 rounded-lg">
                                    <p className="text-sm text-red-700">
                                        <strong>Warning:</strong> Approving this request will permanently delete the patient record.
                                        This action cannot be undone.
                                    </p>
                                </div>

                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Notes (required for rejection)
                                    </label>
                                    <textarea
                                        value={reviewNotes}
                                        onChange={(e) => setReviewNotes(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
                                        rows={3}
                                        placeholder="Enter review notes..."
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={handleRejectRequest}
                                        disabled={formLoading}
                                        className="flex-1 px-4 py-3 bg-white text-red-600 border border-red-200 font-medium rounded-xl hover:bg-red-50 disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        <XCircle className="w-5 h-5" />
                                        Reject
                                    </button>
                                    <button
                                        onClick={handleApproveRequest}
                                        disabled={formLoading}
                                        className="flex-1 px-4 py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        <CheckCircle className="w-5 h-5" />
                                        Approve & Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes slide-in {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .animate-slide-in { animation: slide-in 0.3s ease-out; }
            `}</style>
        </div>
    );
}

